import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from '../api';
import { ApiError, type Friend, type FriendLookup } from '../api/types';
import { t } from '../i18n/idioma';
import { GumpWindow } from './GumpWindow';
import { IcBencao, IcMaisGente } from './icones/IconesV3';

const chaveDe = (nome: string) => nome.trim().toLowerCase();

/**
 * Amigos (techspec-hud-v3 §23): só a lista e o campo de procurar/adicionar. A busca roda com Enter. A conversa
 * saiu daqui: clicar num amigo abre uma aba com o nome dele no chat do jogo (`onConversar`). Convidar para o
 * grupo ou a guilda e remover ficam na própria linha, no mouse.
 */
export function AmigosGump({
  shown, amigos, erroLista, canInviteGuild, podeGrupo, unread,
  onClose, onReload, onConversar, onParty, onGuild
}: {
  shown: boolean;
  amigos: readonly Friend[];
  erroLista: string | null;
  canInviteGuild: boolean;
  podeGrupo: boolean;
  unread: Readonly<Record<string, number>>;
  onClose(): void;
  onReload(): Promise<void> | void;
  onConversar(nome: string): void;
  onParty(nome: string): void;
  onGuild(nome: string): void;
}) {
  const [nome, setNome] = useState('');
  const [achado, setAchado] = useState<FriendLookup | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Remover pede o segundo clique em 3 s (o mesmo jeito do "Sair?" da pílula). */
  const [confirma, setConfirma] = useState<string | null>(null);
  const prazo = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(prazo.current), []);

  const on = amigos.filter(f => f.online);
  const off = amigos.filter(f => !f.online);

  const procurar = async (e: FormEvent) => {
    e.preventDefault();
    setAchado(null);
    const texto = nome.trim();
    if (texto.length < 2) {
      setErro(t('hud.amg.minimo'));
      return;
    }
    setErro(null);
    setBusy(true);
    try {
      setAchado(await api.lookupFriend(texto));
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : t('amigo.naoProcurou'));
    } finally {
      setBusy(false);
    }
  };

  const adicionar = async () => {
    if (!achado) return;
    setBusy(true);
    setErro(null);
    try {
      await api.addFriend(achado.name);
      setAchado(null);
      setNome('');
      await onReload();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : t('amigo.naoAdicionou'));
    } finally {
      setBusy(false);
    }
  };

  const remover = async (f: Friend) => {
    if (confirma !== f.id) {
      setConfirma(f.id);
      window.clearTimeout(prazo.current);
      prazo.current = window.setTimeout(() => setConfirma(null), 3000);
      return;
    }
    window.clearTimeout(prazo.current);
    setConfirma(null);
    setBusy(true);
    setErro(null);
    try {
      await api.removeFriend(f.id);
      await onReload();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : t('amigo.naoRemoveu'));
    } finally {
      setBusy(false);
    }
  };

  const aviso = erro ?? (shown ? erroLista : null);
  const naoLidas = (nomeAmigo: string) => unread[chaveDe(nomeAmigo)] ?? 0;

  const linha = (f: Friend) => (
    <li key={f.id} className="p-amg__item">
      <button type="button" className="friend-row p-amg__row" title={t('tell.falarCom', { nome: f.name })} onClick={() => onConversar(f.name)}>
        <span className={f.online ? 'friend-dot is-on' : 'friend-dot'} />
        <span className="p-amg__nome">{f.name}</span>
        {naoLidas(f.name) > 0 && <span className="kit-pip">{naoLidas(f.name)}</span>}
      </button>
      <span className="p-amg__acoes">
        <button type="button" className="p-amg__acao" disabled={busy || !f.online || !podeGrupo}
          aria-label={`${t('hud.convidarGrupo')} · ${f.name}`} title={t('hud.convidarGrupo')} onClick={() => onParty(f.name)}><IcMaisGente /></button>
        <button type="button" className="p-amg__acao" disabled={busy || !canInviteGuild}
          aria-label={`${t('hud.convidarGuilda')} · ${f.name}`} title={t('hud.convidarGuilda')} onClick={() => onGuild(f.name)}><IcBencao /></button>
        <button type="button" className={`p-amg__acao p-amg__acao--fora${confirma === f.id ? ' is-confirma' : ''}`} disabled={busy}
          aria-label={confirma === f.id ? t('hud.amg.removerConfirma') : `${t('comum.remover')} · ${f.name}`}
          title={t('comum.remover')} onClick={() => void remover(f)}>
          {confirma === f.id ? t('hud.amg.removerConfirma') : '✕'}
        </button>
      </span>
    </li>
  );

  return (
    <GumpWindow
      title={t('amigo.titulo')}
      anchor={{ right: 260, top: 62 }}
      className="p-amg"
      // A posição que o jogador deixou (Renato, 03/11): sem `win--*` na classe, a janela não era guardada e voltava
      // sempre ao canto de nascença.
      posicao="amigos"
      corpoClasse="p-amg__corpo"
      shown={shown}
      snapAnchor
      tituloExtra={<span className="p-amg__conta">{t('hud.amg.conta', { on: on.length, n: amigos.length })}</span>}
      onClose={onClose}
    >
      <form className="p-amg__busca" onSubmit={e => void procurar(e)}>
        <input
          value={nome}
          maxLength={16}
          placeholder={t('hud.amg.adicionarPeloNome')}
          aria-label={t('amigo.nomeNesteShard')}
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="search"
          disabled={busy}
          onChange={e => setNome(e.target.value)}
        />
      </form>
      {achado && (
        <div className="p-amg__achado">
          <span className={achado.online ? 'friend-dot is-on' : 'friend-dot'} />
          <span><b>{achado.name}</b><small>{t(achado.online ? 'hud.amg.onlineAgora' : 'hud.amg.offlineCurto')}</small></span>
          <button type="button" className="btn btn--primary btn--tiny" onClick={() => void adicionar()} disabled={busy}>{t('comum.adicionar')}</button>
        </div>
      )}
      {aviso && <p className="notice p-amg__erro" role="alert">{aviso}</p>}
      <div className="p-amg__listas">
        {amigos.length === 0 && (
          <p className="muted">{t('amigo.vazio')} <small>{t('hud.amg.comoAdicionar', { acao: t('amigo.adicionar') })}</small></p>
        )}
        {on.length > 0 && <><h4>{t('hud.amg.online', { n: on.length })}</h4><ul className="friend-list">{on.map(linha)}</ul></>}
        {off.length > 0 && <><h4>{t('hud.amg.offline', { n: off.length })}</h4><ul className="friend-list">{off.map(linha)}</ul></>}
      </div>
    </GumpWindow>
  );
}
